/**
 * BrandLockup — the canonical logo treatment for the portal.
 *
 * Reads the active brand from `useBranding()` and renders either:
 *  - a standalone wordmark image (when `logoType === 'wordmark'`, e.g. Nuur —
 *    the brand name is already part of the logo), or
 *  - a square mark + the `displayName` as a typographic lockup (when
 *    `logoType === 'mark'`, e.g. Mazeed, or any tenant that only has an icon).
 *
 * This keeps every brand — house or runtime tenant — showing its name
 * prominently and consistently, with no per-brand layout code.
 *
 * Theme: a wordmark bakes its own text colour, so an ink wordmark disappears
 * against the dark sidebar. We render `logoDarkUrl` whenever the document is in
 * dark mode and fall back to the light asset when a brand hasn't supplied one.
 */
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useThemeMode } from '@/hooks/use-theme-mode';
import { useBranding } from './BrandingProvider';

type LockupSize = 'sm' | 'md' | 'lg';

const SIZES: Record<
  LockupSize,
  { wordmark: string; mark: string; name: string; gap: string }
> = {
  sm: { wordmark: 'h-7', mark: 'h-7', name: 'text-base', gap: 'gap-2' },
  md: { wordmark: 'h-8', mark: 'h-8', name: 'text-lg', gap: 'gap-2.5' },
  lg: { wordmark: 'h-12', mark: 'h-11', name: 'text-2xl', gap: 'gap-3' },
};

export interface BrandLockupProps {
  size?: LockupSize;
  className?: string;
  /** Force just the mark (e.g. collapsed sidebar). */
  markOnly?: boolean;
}

export function BrandLockup({
  size = 'md',
  className,
  markOnly = false,
}: Readonly<BrandLockupProps>) {
  const { displayName, branding } = useBranding();
  const theme = useThemeMode();
  const s = SIZES[size];

  const lightSrc = branding?.logoLightUrl || null;
  // A tenant that supplies no dark variant keeps its light one — better a
  // low-contrast logo than none, and `logoType: 'mark'` brands are unaffected.
  const src =
    theme === 'dark' ? branding?.logoDarkUrl || lightSrc : lightSrc;
  // Display prefers the logo asset (usually vector); the favicon is the
  // standalone-mark fallback for a tenant that ships only one.
  const markSrc = src || branding?.faviconUrl || null;
  // A tenant with no logo of its own gets its name, not the portal's legacy
  // `/logo.svg` — which is the Mazeed mark, and a lie over anyone else's name.
  const isWordmark = !!src && (branding?.logoType ?? 'wordmark') === 'wordmark';

  // A tenant-supplied URL can 404. Rather than leave a broken-image glyph in
  // the sidebar, fall back to the typographic lockup.
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src, markSrc]);

  if (markOnly) {
    return failed || !markSrc ? (
      <span
        aria-label={displayName}
        className={cn(
          s.mark,
          'aspect-square grid place-items-center rounded-lg bg-primary font-display font-bold text-primary-foreground',
          className
        )}
      >
        {displayName.charAt(0).toUpperCase()}
      </span>
    ) : (
      <img
        src={markSrc}
        alt={displayName}
        onError={() => setFailed(true)}
        className={cn(s.mark, 'w-auto object-contain', className)}
      />
    );
  }

  // Wordmark image already carries the name — render it alone.
  if (isWordmark && !failed) {
    return (
      <img
        src={src}
        alt={displayName}
        onError={() => setFailed(true)}
        className={cn(s.wordmark, 'w-auto object-contain', className)}
      />
    );
  }

  // Mark + name lockup — also the fallback when a logo image fails to load.
  return (
    <div className={cn('flex items-center', s.gap, className)}>
      {!isWordmark && !failed && markSrc && (
        <img
          src={markSrc}
          alt=""
          aria-hidden="true"
          onError={() => setFailed(true)}
          className={cn(s.mark, 'w-auto shrink-0 object-contain')}
        />
      )}
      <span
        className={cn(
          'font-display font-bold tracking-tight text-foreground leading-none',
          s.name
        )}
      >
        {displayName}
      </span>
    </div>
  );
}

export default BrandLockup;
